import React, { useState, useMemo, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const dropdownVariants = {
    hidden: {
        opacity: 0,
        y: -8,
        scale: 0.96,
        transition: { duration: 0.18, ease: 'easeIn' }
    },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.22,
            ease: 'easeOut',
            staggerChildren: 0.04,
            delayChildren: 0.03
        }
    }
};

const dropdownItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.2, ease: 'easeOut' }
    }
};

const SubHeader = ({ isScrolled, allProducts, onSearchClick, setShowCart, cartItemCount, bundleSettings }) => {
    const [openMenu, setOpenMenu] = useState(null);
    const location = useLocation();
    const { t, lang, toggleLanguage, getCategoryLabel } = useLanguage();

    // Close menus when route changes
    useEffect(() => {
        setOpenMenu(null);
    }, [location]);

    const categories = useMemo(() => {
        if (!allProducts) return [];
        const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
        return uniqueCategories.sort();
    }, [allProducts]);

    const toggleMenu = (menuName) => {
        setOpenMenu(prev => (prev === menuName ? null : menuName));
    };

    return (
        <header className="bg-white shadow-md h-16">
            {/* Invisible Overlay to close menus when clicking outside */}
            {openMenu && (
                <div 
                    className="fixed inset-0 z-10 bg-transparent" 
                    onClick={() => setOpenMenu(null)}
                />
            )}

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full relative z-20">
                <div className="grid grid-cols-3 items-center h-full">
                    
                    {/* Left Side: Animated Logo */}
                    <div className="flex justify-start items-center">
                        <div
                            className={`transition-all duration-200 ease-out ${
                                isScrolled
                                    ? 'opacity-100 translate-x-0 pointer-events-auto'
                                    : 'opacity-0 -translate-x-3 pointer-events-none'
                            }`}
                        >
                            <Link to="/">
                                <img
                                    loading="lazy"
                                    decoding="async"
                                    src="/logo-optimized.webp"
                                    alt="Ukiyo Small Logo"
                                    width="76"
                                    height="48"
                                    className="h-12 w-auto object-contain"
                                />
                            </Link>
                        </div>
                    </div>
                    
                    {/* Center: Navigation */}
                    <nav className="flex justify-center items-center gap-5 sm:gap-8">
                        {/* Products Menu */}
                        <div className="relative">
                            <button
                                onClick={() => toggleMenu('products')}
                                className={`font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${openMenu === 'products' ? 'text-[#8B6939]' : 'text-gray-700 hover:text-[#8B6939]'}`}
                            >
                                {t('products')}
                            </button>
                            <AnimatePresence>
                                {openMenu === 'products' && (
                                    <motion.div
                                        key="products-menu"
                                        variants={dropdownVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-48 bg-white rounded-md shadow-xl border border-gray-100 py-2 overflow-hidden origin-top z-50"
                                    >
                                        <motion.div variants={dropdownItemVariants}>
                                            <NavLink to="/products" className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#8B6939] transition-colors">
                                                {t('allProducts')}
                                            </NavLink>
                                        </motion.div>
                                        <motion.div variants={dropdownItemVariants}>
                                            <NavLink to="/on-sale" className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#8B6939] transition-colors">
                                                {t('onSale')}
                                            </NavLink>
                                        </motion.div>
                                        {bundleSettings?.enabled && (
                                            <motion.div variants={dropdownItemVariants}>
                                                <NavLink to="/bundles" className="block px-4 py-3 text-sm font-semibold text-[#8B6939] hover:bg-gray-50 hover:text-[#b08d57] transition-colors">
                                                    {lang === 'ar' ? (bundleSettings.titleAr || 'الباقات والعروض') : (bundleSettings.titleEn || 'Shop Bundles')}
                                                </NavLink>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Categories Menu */}
                        <div className="relative">
                            <button
                                onClick={() => toggleMenu('categories')}
                                className={`font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${openMenu === 'categories' ? 'text-[#8B6939]' : 'text-gray-700 hover:text-[#8B6939]'}`}
                            >
                                {t('categories')}
                            </button>
                            <AnimatePresence>
                                {openMenu === 'categories' && (
                                    <motion.div
                                        key="category-menu"
                                        variants={dropdownVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-56 bg-white rounded-md shadow-xl border border-gray-100 py-2 max-h-80 overflow-y-auto origin-top z-50"
                                    >
                                        {categories.map(cat => (
                                            <motion.div key={cat} variants={dropdownItemVariants}>
                                                <NavLink 
                                                    to={`/products/category/${cat.toLowerCase().replace(/ /g, '-')}`} 
                                                    className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#8B6939] transition-colors capitalize"
                                                >
                                                    {getCategoryLabel(cat)}
                                                </NavLink>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </nav>

                    {/* Right Side: Animated Language (desktop), Search & Cart */}
                    <div className="flex justify-end items-center gap-1 sm:gap-2">
                        <div
                            className={`flex items-center gap-1 sm:gap-2 transition-all duration-200 ease-out ${
                                isScrolled
                                    ? 'opacity-100 translate-x-0 pointer-events-auto'
                                    : 'opacity-0 translate-x-3 pointer-events-none'
                            }`}
                        >
                            <button
                                type="button"
                                onClick={toggleLanguage}
                                className="hidden sm:inline-flex px-2 py-0.5 text-xs font-semibold text-[#8B6939] border border-[#b08d57]/50 rounded-full hover:bg-[#b08d57] hover:text-white transition-colors"
                            >
                                {t('switchLangShort')}
                            </button>

                            <button onClick={onSearchClick} className="text-gray-800 hover:text-[#8B6939] transition-colors p-2" aria-label="Search">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </button>
                            
                            <button 
                                onClick={() => setShowCart(true)} 
                                className="text-gray-800 hover:text-[#8B6939] transition-colors p-2 relative group"
                                aria-label="Cart"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                                {cartItemCount > 0 && (
                                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-[#b08d57] rounded-full">
                                        {cartItemCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default React.memo(SubHeader);
